/**
 * 预渲染就绪信号。
 *
 * Playwright 在预渲染阶段用 `page.waitForFunction('window.__GEO_READY__ === true')`
 * 判断某个 URL 何时渲染完成，取代不稳定的 `networkidle`（会被字体/图标 CDN 与
 * 统计脚本干扰）。就绪条件为以下两者同时成立：
 *
 * 1. `src/api/request.ts` 维护的在途请求数为 0，且持续 300ms 没有再变为 >0
 *    （debounce：一旦计数在这 300ms 内重新变为 >0，计时器清零重新等待）。
 * 2. 当前页面组件已经调用过至少一次 `useSEO()`（通过 `markSeoInvoked()` 标记）。
 *
 * `window.__GEO_READY__` 只允许从「未就绪」变为 `true`，不会中途被置回 `false`；
 * 路由切换时改用 `resetForNewRoute()` 整体重置状态（包括把 `window.__GEO_READY__`
 * 显式改回一个非 true 值），避免上一页遗留的 `true` 让预渲染器误判新页面已就绪。
 */

import { onPendingRequestCountChange } from "@/api/request";

const READY_DEBOUNCE_MS = 300;

declare global {
  interface Window {
    __GEO_READY__?: boolean;
  }
}

let seoInvoked = false;
let requestsIdleSettled = false;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
// 记录最近一次收到的在途请求数，供 resetForNewRoute() 在重置后重新武装
// debounce 计时器时判断：如果此刻在途数已经是 0，需要立即重新开始等待
// 300ms，而不是傻等一个此后可能永远不会再发生的「计数变化」事件。
let lastKnownPendingCount = 0;

function clearDebounceTimer() {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}

/** 以当前在途请求数重新武装 debounce 计时器（若为 0 则等待 300ms 后置位）。 */
function armDebounceTimer() {
  clearDebounceTimer();
  if (lastKnownPendingCount > 0) {
    return;
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    requestsIdleSettled = true;
    evaluateReady();
  }, READY_DEBOUNCE_MS);
}

function setReady() {
  if (typeof window === "undefined") return;
  // 只允许 false/undefined -> true 这一个方向的迁移。
  window.__GEO_READY__ = true;
}

/** 两个条件都成立时才置位就绪信号；不满足条件时什么都不做（不会把已置位的 true 改回去）。 */
function evaluateReady() {
  if (seoInvoked && requestsIdleSettled) {
    setReady();
  }
}

/** 在途请求数变化时的回调：驱动「0 持续 300ms」这一条件。 */
function handlePendingCountChange(count: number) {
  lastKnownPendingCount = count;
  if (count > 0) {
    requestsIdleSettled = false;
    clearDebounceTimer();
    return;
  }
  armDebounceTimer();
}

// 模块加载即订阅在途请求数变化。`onPendingRequestCountChange` 订阅时会立即
// 以当前值触发一次回调，等价于启动时就跑一次判定。
onPendingRequestCountChange(handlePendingCountChange);

/**
 * 标记当前页面组件已调用过一次 `useSEO()`。
 *
 * 集成点：`src/utils/seo.ts` 的 `useSEO()` 应在其 effect 中调用一次本函数。
 * 该调用未接入 `useSEO()` 本身——task 11.1（改造 `src/utils/seo.ts` 委托到
 * `src/seo/*`）与本任务并行且尚未完成，为避免冲突未在此处直接修改 `seo.ts`。
 * 待 11.1 落地后，需要在 `useSEO()` 的 effect 中补一行 `markSeoInvoked()` 调用。
 */
export function markSeoInvoked(): void {
  seoInvoked = true;
  evaluateReady();
}

/**
 * 路由切换时调用：重置「本页是否已调用 useSEO」状态，并把就绪信号改回未就绪，
 * 避免上一页遗留的 `true` 让预渲染器对新页面误判为已就绪。
 *
 * 集成点：路由切换钩子（例如 `App.tsx` 中已监听 `pathname` 变化的
 * `ScrollToTop` 组件）应调用本函数。本任务只导出该函数，接入路由由后续任务
 * 完成，理由同上——避免与并行任务冲突。
 */
export function resetForNewRoute(): void {
  seoInvoked = false;
  requestsIdleSettled = false;
  if (typeof window !== "undefined") {
    window.__GEO_READY__ = false;
  }
  // 重新开始「在途数为 0 持续 300ms」的计时（若此刻仍有在途请求，
  // armDebounceTimer() 会直接跳过，等下一次计数变化事件再驱动）。
  armDebounceTimer();
}

/** 仅供测试使用：读取当前「本页是否已调用 useSEO」的内部状态。 */
export function __isSeoInvokedForTest(): boolean {
  return seoInvoked;
}
